import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { PlanAdmin } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { Nombre } from '../components/Nombre'
import { Badge, Button, Card, ConfirmDialog, DataTable, PageHeader, Stack, useToast } from '../components/ui'
import type { Column } from '../components/ui'
import { nomFormule } from './formules'
import { BillingPolicyCard } from './BillingPolicyCard'

/**
 * Les formules de la plateforme : ce qui se vend, à quel prix, et avec quoi dedans.
 *
 * ARCHIVER N'EST PAS SUPPRIMER, et la confirmation le dit avec le nombre d'abonnés. Une
 * formule archivée disparaît de la création de client et du changement de formule ; ses
 * abonnés en place gardent tout — formule, prix, fonctionnalités. Sans le chiffre, on
 * croirait couper quelque chose à des clients, ou au contraire on archiverait sans savoir
 * que trente clients en dépendent.
 *
 * TROIS ÉTATS ET NON DEUX. Publique : proposée au catalogue. Masquée : vendable, mais seul
 * le panneau la propose — c'est l'état d'une formule négociée ou en préparation. Archivée :
 * plus vendable du tout.
 */
export function PlansPage() {
  const { t, language, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [plans, setPlans] = useState<PlanAdmin[]>([])
  const [loading, setLoading] = useState(true)
  const [cible, setCible] = useState<PlanAdmin | null>(null)
  const [bascule, setBascule] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return

    setLoading(true)

    api
      .listPlatformPlans(token)
      .then(setPlans)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setLoading(false))
  }, [token, notify, describeError])

  useEffect(charger, [charger])

  const confirmer = async () => {
    if (cible === null) return

    setBascule(true)

    try {
      await api.setPlatformPlanActive(token, cible.id, !cible.isActive)
      notify(t(cible.isActive ? 'platform.plans.archived' : 'platform.plans.restored'), 'succes')
      setCible(null)
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setBascule(false)
    }
  }

  const colonnes: Column<PlanAdmin>[] = [
    {
      key: 'name',
      header: t('platform.plans.name'),
      render: (p) => (
        <span className="flex flex-col">
          <Link className="underline" to={`/platform/plans/${p.id}`}>
            {nomFormule(language, p)}
          </Link>
          <span className="text-[0.8rem] text-texte-doux" dir="ltr">
            {p.code}
          </span>
        </span>
      ),
    },
    {
      key: 'price',
      header: t('platform.plans.price'),
      numeric: true,
      render: (p) => (
        <span>
          <Nombre valeur={p.price} decimales={3} unite={p.currencyCode} />{' '}
          <span className="text-texte-doux">/ {t(p.billingPeriod === 'monthly' ? 'platform.plans.perMonth' : 'platform.plans.perYear')}</span>
        </span>
      ),
    },
    {
      key: 'content',
      header: t('platform.plans.content'),
      render: (p) => (
        <span className="flex flex-wrap gap-1">
          {p.modules.map((m) => (
            <Badge key={m} tone="info">
              {t(`module.${m}` as never)}
            </Badge>
          ))}
          {p.features.map((f) => (
            <Badge key={f} tone="succes">
              {t(`feature.${f}` as never)}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      key: 'subscribers',
      header: t('platform.plans.subscribers'),
      numeric: true,
      render: (p) => <Nombre valeur={p.liveSubscriptions} decimales={0} />,
    },
    {
      key: 'status',
      header: t('platform.plans.status'),
      render: (p) =>
        !p.isActive ? (
          <Badge tone="avertissement">{t('platform.plans.state.archived')}</Badge>
        ) : p.isPublic ? (
          <Badge tone="succes">{t('platform.plans.state.public')}</Badge>
        ) : (
          <Badge tone="info">{t('platform.plans.state.hidden')}</Badge>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (p) => (
        <span className="flex flex-wrap gap-2">
          <Link className="btn ghost" to={`/platform/plans/${p.id}`}>
            {t('common.edit')}
          </Link>
          <Button variant={p.isActive ? 'danger' : 'secondaire'} onClick={() => setCible(p)}>
            {t(p.isActive ? 'platform.plans.archive' : 'platform.plans.restore')}
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Stack gap={4}>
      <PageHeader
        title={t('platform.plans.title')}
        description={t('platform.plans.description')}
        actions={
          <Link className="btn" to="/platform/plans/nouvelle">
            {t('platform.plans.new')}
          </Link>
        }
      />

      <Card title={t('platform.plans.list')}>
        {loading ? (
          <p className="text-texte-doux">{t('common.loading')}</p>
        ) : (
          <DataTable columns={colonnes} rows={plans} rowKey={(p) => p.id} emptyLabel={t('platform.plans.empty')} />
        )}
      </Card>

      <BillingPolicyCard />

      <ConfirmDialog
        open={cible !== null}
        busy={bascule}
        title={t(cible?.isActive === true ? 'platform.plans.archive.title' : 'platform.plans.restore.title')}
        message={
          cible === null
            ? ''
            : cible.isActive
              ? `${nomFormule(language, cible)} — ${t('platform.plans.archive.message')} ${t('platform.plans.archive.subscribers')} ${cible.liveSubscriptions}.`
              : `${nomFormule(language, cible)} — ${t('platform.plans.restore.message')}`
        }
        confirmLabel={t(cible?.isActive === true ? 'platform.plans.archive' : 'platform.plans.restore')}
        onConfirm={() => void confirmer()}
        onCancel={() => setCible(null)}
      />
    </Stack>
  )
}
